import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend,
    Filler
);

const EndpointPerformanceChart = ({ endpoints }) => {
    if (!endpoints || endpoints.length === 0) {
        return null;
    }

    // Prepare labels (truncated URLs for readability)
    const getShortLabel = (endpoint) => {
        const url = endpoint.url;
        try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/').filter(Boolean);
            if (pathParts.length > 0) {
                return `${endpoint.method} /${pathParts.slice(-2).join('/')}`;
            }
            return `${endpoint.method} ${urlObj.hostname}`;
        } catch {
            // If URL parsing fails, just use a truncated version
            const truncated = url.length > 30 ? url.substring(0, 30) + '...' : url;
            return `${endpoint.method} ${truncated}`;
        }
    };

    const labels = endpoints.map(getShortLabel);

    // Extract metrics from endpoints
    const avgResponseTimes = endpoints.map(e => e.stats?.avgResponseTime || 0);
    const successRates = endpoints.map(e => e.stats?.successRate || 0);
    const totalRequests = endpoints.map(e => e.stats?.totalRequests || 0);

    // Calculate requests per second (using totalRequests as a proxy since we don't have duration data)
    // For display purposes, we'll show total requests normalized
    const maxRequests = Math.max(...totalRequests, 1);
    const normalizedRequests = totalRequests.map(r => (r / maxRequests) * 100);

    // Combined chart data with multiple y-axes
    const combinedChartData = {
        labels,
        datasets: [
            {
                type: 'line',
                label: 'Avg Response Time (ms)',
                data: avgResponseTimes,
                borderColor: 'rgba(59, 130, 246, 1)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderWidth: 3,
                fill: true,
                tension: 0.4,
                pointRadius: 6,
                pointHoverRadius: 8,
                pointBackgroundColor: 'rgba(59, 130, 246, 1)',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                yAxisID: 'y',
            },
            {
                type: 'line',
                label: 'Success Rate (%)',
                data: successRates,
                borderColor: 'rgba(34, 197, 94, 1)',
                backgroundColor: 'rgba(34, 197, 94, 0.05)',
                borderWidth: 3,
                fill: false,
                tension: 0.4,
                pointRadius: 6,
                pointHoverRadius: 8,
                pointBackgroundColor: 'rgba(34, 197, 94, 1)',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                yAxisID: 'y1',
            },
            {
                type: 'bar',
                label: 'Total Requests (normalized)',
                data: normalizedRequests,
                backgroundColor: 'rgba(156, 163, 175, 0.5)',
                borderColor: 'rgba(156, 163, 175, 0.8)',
                borderWidth: 1,
                borderRadius: 4,
                yAxisID: 'y1',
            },
        ],
    };

    const combinedChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        size: 12,
                        weight: '500',
                    },
                    color: 'var(--text-primary)',
                },
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                titleFont: {
                    size: 13,
                    weight: '600',
                },
                bodyFont: {
                    size: 12,
                },
                padding: 12,
                cornerRadius: 8,
                callbacks: {
                    title: (context) => {
                        const index = context[0].dataIndex;
                        return endpoints[index]?.url || '';
                    },
                    label: (context) => {
                        const label = context.dataset.label;
                        const value = context.raw;
                        if (label.includes('Response Time')) {
                            return `${label}: ${value.toLocaleString()}ms`;
                        } else if (label.includes('Success Rate')) {
                            return `${label}: ${value.toFixed(1)}%`;
                        } else if (label.includes('Total Requests')) {
                            const actualValue = totalRequests[context.dataIndex];
                            return `Total Requests: ${actualValue.toLocaleString()}`;
                        }
                        return `${label}: ${value}`;
                    },
                },
            },
        },
        scales: {
            x: {
                ticks: {
                    color: 'var(--text-secondary)',
                    font: {
                        size: 11,
                    },
                    maxRotation: 45,
                    minRotation: 0,
                },
                grid: {
                    display: false,
                },
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Response Time (ms)',
                    color: 'rgba(59, 130, 246, 1)',
                    font: {
                        size: 12,
                        weight: '600',
                    },
                },
                ticks: {
                    color: 'rgba(59, 130, 246, 1)',
                    font: {
                        size: 11,
                    },
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                },
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                min: 0,
                max: 100,
                title: {
                    display: true,
                    text: '% / Normalized',
                    color: 'rgba(34, 197, 94, 1)',
                    font: {
                        size: 12,
                        weight: '600',
                    },
                },
                ticks: {
                    color: 'rgba(34, 197, 94, 1)',
                    font: {
                        size: 11,
                    },
                    callback: (value) => value + '%',
                },
                grid: {
                    drawOnChartArea: false,
                },
            },
        },
    };

    // Summary stats for quick metrics display
    const avgOverallResponseTime = avgResponseTimes.length > 0
        ? (avgResponseTimes.reduce((a, b) => a + b, 0) / avgResponseTimes.length).toFixed(0)
        : 0;

    const avgSuccessRate = successRates.length > 0
        ? (successRates.reduce((a, b) => a + b, 0) / successRates.length).toFixed(1)
        : 0;

    const totalRequestsSum = totalRequests.reduce((a, b) => a + b, 0);
    const avgErrorRate = (100 - parseFloat(avgSuccessRate)).toFixed(2);

    const formatNumber = (num) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(2) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(2) + 'K';
        }
        return num.toString();
    };

    return (
        <div className="performance-chart-section">
            <style>{`
        .performance-chart-section {
          margin-top: 30px;
          margin-bottom: 30px;
        }
        
        .performance-chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 16px;
        }
        
        .chart-section-title {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .chart-quick-stats {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
        }
        
        .quick-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 8px 16px;
          background: var(--light-gray);
          border-radius: 8px;
          min-width: 100px;
        }
        
        .quick-stat-value {
          font-size: 18px;
          font-weight: 700;
          color: var(--text-primary);
        }
        
        .quick-stat-value.warning {
          color: var(--warning);
        }
        
        .quick-stat-value.success {
          color: var(--success);
        }
        
        .quick-stat-value.error {
          color: var(--error);
        }
        
        .quick-stat-label {
          font-size: 11px;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 2px;
        }
        
        .performance-chart-container {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 24px;
        }
        
        .chart-wrapper {
          height: 350px;
          position: relative;
        }
        
        .chart-legend-note {
          display: flex;
          justify-content: center;
          gap: 20px;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid var(--border-color);
          flex-wrap: wrap;
        }
        
        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--text-secondary);
        }
        
        .legend-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }
        
        .legend-dot.blue {
          background: rgba(59, 130, 246, 1);
        }
        
        .legend-dot.green {
          background: rgba(34, 197, 94, 1);
        }
        
        .legend-dot.gray {
          background: rgba(156, 163, 175, 0.8);
          border-radius: 2px;
        }
        
        @media (max-width: 768px) {
          .performance-chart-header {
            flex-direction: column;
            align-items: stretch;
          }
          
          .chart-quick-stats {
            justify-content: center;
          }
          
          .quick-stat {
            flex: 1;
            min-width: 80px;
          }
          
          .chart-wrapper {
            height: 300px;
          }
          
          .performance-chart-container {
            padding: 16px;
          }
        }
        
        @media (max-width: 480px) {
          .chart-wrapper {
            height: 250px;
          }
          
          .quick-stat-value {
            font-size: 16px;
          }
          
          .quick-stat-label {
            font-size: 10px;
          }
        }
      `}</style>

            <div className="performance-chart-header">
                <h2 className="chart-section-title">Endpoint Performance Overview</h2>
                <div className="chart-quick-stats">
                    <div className="quick-stat">
                        <span className="quick-stat-value">{avgOverallResponseTime}ms</span>
                        <span className="quick-stat-label">Avg Response</span>
                    </div>
                    <div className="quick-stat">
                        <span className={`quick-stat-value ${parseFloat(avgSuccessRate) >= 95 ? 'success' : parseFloat(avgSuccessRate) >= 80 ? 'warning' : 'error'}`}>
                            {avgSuccessRate}%
                        </span>
                        <span className="quick-stat-label">Success Rate</span>
                    </div>
                    <div className="quick-stat">
                        <span className={`quick-stat-value ${parseFloat(avgErrorRate) === 0 ? 'success' : parseFloat(avgErrorRate) < 5 ? 'warning' : 'error'}`}>
                            {avgErrorRate}%
                        </span>
                        <span className="quick-stat-label">Error Rate</span>
                    </div>
                    <div className="quick-stat">
                        <span className="quick-stat-value">{formatNumber(totalRequestsSum)}</span>
                        <span className="quick-stat-label">Total Requests</span>
                    </div>
                </div>
            </div>

            <div className="performance-chart-container">
                <div className="chart-wrapper">
                    <Line data={combinedChartData} options={combinedChartOptions} />
                </div>
                <div className="chart-legend-note">
                    <div className="legend-item">
                        <span className="legend-dot blue"></span>
                        <span>Response Time (left axis, ms)</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-dot green"></span>
                        <span>Success Rate (right axis, %)</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-dot gray"></span>
                        <span>Request Volume (relative)</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EndpointPerformanceChart;
